import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { FormGroup } from "@angular/forms";
import { environment } from 'src/environments/environment';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class Utils {
  constructor(private http: HttpClient, private breakpointObserver: BreakpointObserver) {}

  /**
   * Carga cualquier JSON ubicado en assets/data/
   * @param fileName Nombre del archivo sin extensión (ej: "addressTypes")
   * @returns Observable con el contenido del JSON
   */
  async loadJsonArray<T>(fileName: string): Promise<T[]> {
    const path = `assets/data/${fileName}.json`;

    try {
      const data = await this.http.get<T[]>(path).toPromise();
      return data ?? [];
    } catch (error) {
      console.error(`Error cargando ${fileName}.json`, error);
      return [];
    }
  }

  /**
   * Monitorea los cambios del formulario y muestra errores
   * solo en ambiente de desarrollo.
   */
  debugForm(form: FormGroup, formName: string = "Form") {
    
    if (environment.production) return; // ❌ No se ejecuta en producción

    form.valueChanges.subscribe(() => {
      Object.keys(form.controls).forEach((field) => {
        const control = form.get(field);
      });
    });
  }

  validNIE(nie: any) {
    let niePrefix = null;
    const nifRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKET]{1}$/i;
    const nifLetters = "TRWAGMYFPDXBNJZSQVHLCKE";
    let valid = false;

    nie = nie.toUpperCase();
    if (9 === nie.length) {
      niePrefix = nie.charAt(0);
      switch (niePrefix) {
        case "X":
          niePrefix = 0;
          break;
        case "Y":
          niePrefix = 1;
          break;
        case "Z":
          niePrefix = 2;
          break;
        default:
          niePrefix = null;
          break;
      }
      nie = niePrefix + nie.substr(1);
      if (nie.match(nifRegex)) {
        valid = nie.charAt(8) === nifLetters.charAt(parseInt(nie, 10) % 23);
      }
    }

    return valid;
  }

  isNullOrEmpy(cadena) {
    if (cadena != null && cadena !== "") {
      return false;
    }
    return true;
  }

  validatedc(c1: any, c2: any, c3: any, c4: any, digitoControl: any): any {
    let parte1, parte2;

    parte1 = c1 + c2;

    parte2 = c4;

    let Apesos = new Array(1, 2, 4, 8, 5, 10, 9, 7, 3, 6);
    let DC1 = 0;
    let DC2 = 0;

    let x = 8;
    while (x > 0) {
      let digito = parte1.substr(x - 1, 1);
      DC1 = DC1 + Apesos[x + 1] * digito;
      x = x - 1;
    }

    let Resto = DC1 % 11;
    DC1 = 11 - Resto;

    if (DC1 == 10) {
      DC1 = 1;
    }
    if (DC1 == 11) {
      DC1 = 0;
    }

    x = 10;
    while (x > 0) {
      let digito = parte2.substr(x - 1, 1);
      DC2 = DC2 + Apesos[x - 1] * digito;
      x = x - 1;
    }

    Resto = DC2 % 11;
    DC2 = 11 - Resto;

    if (DC2 == 10) {
      DC2 = 1;
    }
    if (DC2 == 11) {
      DC2 = 0;
    }

    let DC = DC1 + "" + DC2;

    if (DC != digitoControl) {
      return false;
    } else {
      return true;
    }
  }

  validate(control: any): any {
    const codeLengths = {
      AD: 24,
      AE: 23,
      AL: 28,
      AT: 20,
      AZ: 28,
      BA: 20,
      BE: 16,
      BG: 22,
      BH: 22,
      BR: 29,
      CH: 21,
      CR: 21,
      CY: 28,
      CZ: 24,
      DE: 22,
      DK: 18,
      DO: 28,
      EE: 20,
      ES: 24,
      LC: 30,
      FI: 18,
      FO: 18,
      FR: 27,
      GB: 22,
      GI: 23,
      GL: 18,
      GR: 27,
      GT: 28,
      HR: 21,
      HU: 28,
      IE: 22,
      IL: 23,
      IS: 26,
      IT: 27,
      JO: 30,
      KW: 30,
      KZ: 20,
      LB: 28,
      LI: 21,
      LT: 20,
      LU: 20,
      LV: 21,
      MC: 27,
      MD: 24,
      ME: 22,
      MK: 19,
      MR: 27,
      MT: 31,
      MU: 30,
      NL: 18,
      NO: 15,
      PK: 24,
      PL: 28,
      PS: 29,
      PT: 25,
      QA: 29,
      RO: 24,
      RS: 22,
      SA: 24,
      SE: 24,
      SI: 19,
      SK: 24,
      SM: 27,
      TN: 24,
      TR: 26,
    };
    const iban = control.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]{20})$/);
    let digits: number;
    if (!code || iban.length !== codeLengths[code[1]]) {
      return { ibanValid: false };
    }
    digits = (code[3] + code[1] + code[2]).replace(
      /[A-Z]/g,
      (letter: string) => {
        return letter.charCodeAt(0) - 55;
      }
    );
    return this.mod97(digits) === 1
      ? { ibanValid: true }
      : { ibanValid: false };
  }

  mod97(digital: number | string) {
    digital = digital.toString();
    let checksum: number | string = digital.slice(0, 2);
    let fragment = "";
    for (let offset = 2; offset < digital.length; offset += 7) {
      fragment = checksum + digital.substring(offset, offset + 7);
      checksum = parseInt(fragment, 10) % 97;
    }
    return checksum;
  }

  validCIF(cif: any) {
    var cif = cif.toUpperCase();
    let v_letras_cif = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let v_longitud = cif.length;
    let v_letra = cif.substring(0, 1);
    let v_numero = cif.substring(1, v_longitud - 1);
    let v_ultimo_num = cif.substring(v_longitud - 1);

    if (v_letras_cif.indexOf(v_letra) == -1) {
      return false;
    }

    if (!/^[0-9A-J]$/.test(v_ultimo_num)) {
      return false;
    }

    let v_numerico;
    try {
      v_numerico = parseInt(v_numero);
    } catch (e) {
      return false;
    }

    let v_suma_par = 0;
    let v_suma_imp = 0;
    for (let i = 1; i <= 7; i++) {
      if (i % 2 == 0) {
        v_suma_par += parseInt(v_numero.substring(i - 1, i));
      } else {
        let v_suma_aux = parseInt(v_numero.substring(i - 1, i)) * 2;
        v_suma_aux = Math.trunc(v_suma_aux / 10) + (v_suma_aux % 10);
        v_suma_imp += v_suma_aux;
      }
    }

    let v_dg_control = 10 - ((v_suma_par + v_suma_imp) % 10);
    if (v_dg_control == 10) {
      v_dg_control = 0;
    }
    let v_letra_control = "";
    switch (v_dg_control) {
      case 1:
        v_letra_control = "A";
        break;
      case 2:
        v_letra_control = "B";
        break;
      case 3:
        v_letra_control = "C";
        break;
      case 4:
        v_letra_control = "D";
        break;
      case 5:
        v_letra_control = "E";
        break;
      case 6:
        v_letra_control = "F";
        break;
      case 7:
        v_letra_control = "G";
        break;
      case 8:
        v_letra_control = "H";
        break;
      case 9:
        v_letra_control = "I";
        break;
      case 0:
        v_letra_control = "J";
        break;
    }
    if (
      v_ultimo_num != v_dg_control.toString() &&
      v_ultimo_num != v_letra_control
    ) {
      return false;
    } else {
      return true;
    }
  }

  validNIF(nif: any) {
    const nifRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]{1}$/i;
    const nifLetters = "TRWAGMYFPDXBNJZSQVHLCKE";
    let valid = false;

    nif = nif.toUpperCase();
    if (9 === nif.length) {
      if (nif.match(nifRegex)) {
        valid = nif.charAt(8) === nifLetters.charAt(parseInt(nif, 10) % 23);
      }
    }

    return valid;
  }

  // ==================== MÉTODOS COMPARTIDOS PARA PAÍSES Y PREFIJOS ====================
  
  // ISO2 codes for EU member states
  private EU_ISO2: string[] = [
    'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE'
  ];

  // Map from country name (lower) to iso2 code
  private countryNameToCode: { [name: string]: string } = {};

  /**
   * Inicializa el mapa de países para convertir nombres a códigos ISO2
   * @param countries Array de países con propiedades name y code
   */
  initializeCountryMap(countries: any[]): void {
    this.countryNameToCode = {};
    countries.forEach(c => {
      if (c && c.name && c.code) {
        this.countryNameToCode[(c.name || '').toLowerCase()] = (c.code || '').toUpperCase();
      }
    });
  }

  /**
   * Convierte un código ISO2 a emoji de bandera
   * @param iso2 Código ISO2 del país (ej: 'ES', 'FR')
   * @returns Emoji de la bandera o cadena vacía
   */
  iso2ToEmoji(iso2: string): string {
    if (!iso2 || iso2.length !== 2) return '';
    const A = 0x1f1e6;
    const first = iso2.charCodeAt(0);
    const second = iso2.charCodeAt(1);
    // A-Z
    if (first < 65 || first > 90 || second < 65 || second > 90) return '';
    return String.fromCodePoint(A + (first - 65), A + (second - 65));
  }

  /**
   * Obtiene el emoji de bandera dado un objeto de prefijo
   * @param prefixObj Objeto con propiedades name y prefix
   * @param countries Array de países
   * @returns Emoji de la bandera o cadena vacía
   */
  flagForPrefix(prefixObj: any, countries: any[]): string {
    if (!prefixObj) return '';
    const name = (prefixObj.name || '').toLowerCase();
    // direct match
    const iso = this.countryNameToCode[name];
    if (iso) return this.iso2ToEmoji(iso);
    // some names in prefixes.json may include accents or different words, try alternatives
    // try find country whose name startsWith the prefix name or viceversa
    const found = countries.find(c => (c.name || '').toLowerCase().includes(name) || name.includes((c.name || '').toLowerCase()));
    if (found && found.code) return this.iso2ToEmoji(found.code.toUpperCase());
    return '';
  }

  /**
   * Helper para template: dado el valor del prefijo seleccionado retorna el emoji
   * @param prefixValue Valor del prefijo (ej: '+34')
   * @param prefixes Array de prefijos
   * @param countries Array de países
   * @returns Emoji de la bandera o cadena vacía
   */
  flagForPrefixValue(prefixValue: string, prefixes: any[], countries: any[]): string {
    if (!prefixValue) return '';
    const p = prefixes.find(x => x.prefix === prefixValue);
    return this.flagForPrefix(p, countries);
  }

  downloadTemplate(headers : string, nameTemplate : string) {
    const header = headers;
    const blob = new Blob([header], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", nameTemplate + ".csv");
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  getModalWidth(): string {
    if (window.innerWidth < 576) {
      return "95vw"; // Extra small devices
    } else if (window.innerWidth < 768) {
      return "90vw"; // Small devices
    } else if (window.innerWidth < 992) {
      return "600px"; // Medium devices
    } else {
      return "700px"; // Large devices and up
    }
  }

  public isMobile$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay(1)
    );

  public isTablet$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Tablet)
    .pipe(
      map(result => result.matches),
      shareReplay(1)
    );

  public isDesktop$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Web)
    .pipe(
      map(result => result.matches),
      shareReplay(1)
    );
}
